/* ------------------------------------------------------------------------------------------
 * A contract's pick-up / drop-off in an Excel export: a REAL Excel date cell (owner, 27 Sep 2026).
 * Written as "dd/mm/yyyy" text, Excel sorts and filters it as text - 01/10/2026 comes before
 * 02/09/2026. A date cell is a number (days since 30/12/1899) with the "dd/mm/yyyy hh:mm" format:
 * it shows exactly what the page shows and sorts by date and time.
 *
 * The value is read by formatContractDateTime (the one reader of contract dates), so the cell shows
 * the same Dubai wall-clock time as the page, whatever the browser's time zone, and a value with
 * no time gets "dd/mm/yyyy" - a time is never made up. A value that cannot be read stays as it
 * was (text such as "Open" included).
 *
 * SheetJS (xlsx) takes a cell object as it is in json_to_sheet / aoa_to_sheet. The community
 * build writes no alignment, so these cells keep Excel's own alignment, like every other cell
 * of those files.
 * ---------------------------------------------------------------------------------------- */
import { formatContractDateTime } from './contractDateUtils';

export const EXCEL_DATE_TIME_FORMAT = 'dd/mm/yyyy hh:mm';
export const EXCEL_DATE_FORMAT = 'dd/mm/yyyy';

const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30);
const DAY_MS = 86400000;
const SHOWN = /^(\d{2})\/(\d{2})\/(\d{4})(?: (\d{2}):(\d{2}))?$/;

/**
 * A contract date -> { serial, format, text } for Excel, or null when it cannot be read.
 * `serial` = Excel's day number (the time as the fraction), `text` = what the page shows.
 */
export function contractExcelDate(value) {
    const text = formatContractDateTime(value);
    const m = SHOWN.exec(text);
    if (!m) return null;
    const hasTime = m[4] !== undefined;
    // the wall-clock parts summed as UTC: no browser time zone enters the number
    const ms = Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1]), hasTime ? Number(m[4]) : 0, hasTime ? Number(m[5]) : 0);
    return { serial: (ms - EXCEL_EPOCH_MS) / DAY_MS, format: hasTime ? EXCEL_DATE_TIME_FORMAT : EXCEL_DATE_FORMAT, text };
}

/**
 * The SheetJS cell of a contract date: a date cell, or `fallback` when the value cannot be read
 * (by default the value itself, '' when empty).
 */
export function contractDateSheetCell(value, fallback) {
    const x = contractExcelDate(value);
    if (x) return { t: 'n', v: x.serial, z: x.format };
    if (fallback !== undefined) return fallback;
    return value === null || value === undefined ? '' : value;
}

// "AB12" -> 27 (0-based column)
const columnOf = (ref) => {
    const letters = /^[A-Z]+/.exec(ref);
    if (!letters) return -1;
    let c = 0;
    for (const ch of letters[0]) c = c * 26 + (ch.charCodeAt(0) - 64);
    return c - 1;
};

/**
 * Widen the columns that hold contract date cells: a date too wide for its column shows "#####"
 * in Excel (text spills into the next cell, a number does not). Returns the sheet.
 */
export function fitContractDateColumns(ws) {
    if (!ws) return ws;
    const cols = ws['!cols'] ? [...ws['!cols']] : [];
    let changed = false;
    Object.keys(ws).forEach((ref) => {
        if (ref[0] === '!') return;
        const cell = ws[ref];
        if (!cell || (cell.z !== EXCEL_DATE_TIME_FORMAT && cell.z !== EXCEL_DATE_FORMAT)) return;
        const c = columnOf(ref);
        const want = cell.z === EXCEL_DATE_TIME_FORMAT ? 18 : 12;
        if (c >= 0 && !((cols[c] && cols[c].wch) >= want)) {
            cols[c] = { ...(cols[c] || {}), wch: want };
            changed = true;
        }
    });
    if (changed) ws['!cols'] = cols;       // SheetJS skips the columns left out (Excel's own width)
    return ws;
}
